import type { OutboxStatusDto, SettingDto } from '@deli/contracts';
import { useState, type FormEvent } from 'react';
import {
  useDiscardBacklog,
  useN8nStatus,
  useN8nTest,
  useSaveSetting,
  useSettings,
  useStoreStatus,
} from '../api/staff.js';
import { ErrorText } from '../components/ErrorText.js';
import { QueryState } from '../components/QueryState.js';
import { formatDateTime } from '../format.js';
import { t } from '../i18n/uk.js';

/** One setting. Secrets are write-only: the field always starts empty and only "set" is shown. */
function SettingRow({ setting }: { setting: SettingDto }) {
  const save = useSaveSetting();
  const initial = setting.kind === 'secret' || setting.value === null ? '' : String(setting.value);
  const [value, setValue] = useState(initial);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed =
      setting.kind === 'integer' ? Number(value) : value.trim() === '' ? null : value.trim();
    save.mutate(
      { key: setting.key, value: parsed },
      { onSuccess: () => setting.kind === 'secret' && setValue('') },
    );
  };

  return (
    <form className="setting" onSubmit={submit}>
      <div>
        <div className="setting-name">{setting.description}</div>
        <code className="muted">{setting.key}</code>
        {setting.kind === 'integer' && (
          <div className="muted">
            {t.settings.default}: {setting.default} · {t.settings.range}: {setting.min}–
            {setting.max}
          </div>
        )}
        {setting.updatedAt && (
          <div className="muted">
            {t.settings.lastChanged}: {formatDateTime(setting.updatedAt)}
          </div>
        )}
      </div>
      <div className="setting-input">
        {setting.kind === 'secret' && (
          <span className={setting.isSet ? 'badge ok' : 'badge bad'}>
            {setting.isSet ? t.settings.isSet : t.settings.notSet}
          </span>
        )}
        {setting.kind === 'text' ? (
          <textarea
            aria-label={setting.key}
            rows={4}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        ) : (
          <input
            aria-label={setting.key}
            type={
              setting.kind === 'integer' ? 'number' : setting.kind === 'secret' ? 'password' : 'url'
            }
            autoComplete="off"
            placeholder={setting.kind === 'secret' ? t.settings.newSecret : undefined}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
        <button type="submit" disabled={save.isPending}>
          {t.common.save}
        </button>
        {save.isSuccess && <span className="muted">{t.common.saved}</span>}
        <ErrorText error={save.error} />
      </div>
    </form>
  );
}

/** Where one webhook destination stands: configured or not, and its outbox counts. */
function DeliveryFacts({ s }: { s: OutboxStatusDto }) {
  return (
    <dl className="facts">
      <dt>{t.settings.webhook}</dt>
      <dd>
        <span className={s.webhookConfigured ? 'badge ok' : 'badge bad'}>
          {s.webhookConfigured ? t.settings.configured : t.settings.notConfigured}
        </span>
      </dd>
      <dt>{t.settings.secret}</dt>
      <dd>
        <span className={s.secretConfigured ? 'badge ok' : 'badge bad'}>
          {s.secretConfigured ? t.settings.configured : t.settings.notConfigured}
        </span>
      </dd>
      <dt>{t.settings.pendingEvents}</dt>
      <dd>{s.pending}</dd>
      <dt>{t.settings.failedEvents}</dt>
      <dd>{s.failed}</dd>
      <dt>{t.settings.lastSent}</dt>
      <dd>{formatDateTime(s.lastSentAt)}</dd>
    </dl>
  );
}

function N8nPanel() {
  const status = useN8nStatus();
  const test = useN8nTest();
  const discard = useDiscardBacklog();
  return (
    <div className="card">
      <h2>{t.settings.n8nTitle}</h2>
      <p className="muted">{t.settings.n8nIntro}</p>
      <QueryState query={status}>{(s) => <DeliveryFacts s={s} />}</QueryState>
      <div className="actions">
        <button
          type="button"
          className="primary"
          onClick={() => test.mutate()}
          disabled={test.isPending}
        >
          {t.settings.sendTest}
        </button>
        <button
          type="button"
          onClick={() => discard.mutate()}
          disabled={discard.isPending || status.data?.pending === 0}
        >
          {t.settings.discard}
        </button>
      </div>
      {test.data && (
        <p role="status" className={test.data.delivered ? 'ok-text' : 'error-text'}>
          {test.data.delivered
            ? `${t.settings.testDelivered} (HTTP ${test.data.status})`
            : `${t.settings.testFailed}: ${test.data.error}`}
        </p>
      )}
      {discard.data && (
        <p className="muted">
          {t.settings.discarded}: {discard.data.discarded}
        </p>
      )}
      <ErrorText error={test.error ?? discard.error} />
    </div>
  );
}

/** The DeliStore plugin's webhook (prices, stock and store order statuses). */
function StorePanel() {
  const status = useStoreStatus();
  return (
    <div className="card">
      <h2>{t.settings.storeTitle}</h2>
      <p className="muted">{t.settings.storeIntro}</p>
      <QueryState query={status}>{(s) => <DeliveryFacts s={s} />}</QueryState>
    </div>
  );
}

export function SettingsPage() {
  const settings = useSettings();
  return (
    <section>
      <h1>{t.settings.title}</h1>
      <QueryState query={settings}>
        {(list) => (
          <div className="card">
            {list.map((setting) => (
              <SettingRow key={setting.key} setting={setting} />
            ))}
          </div>
        )}
      </QueryState>
      <N8nPanel />
      <StorePanel />
    </section>
  );
}
